import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import _ from 'lodash';
import { useAuth } from 'src/state/auth';
import { TableUserItem } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { useRemoveUserMutation, WorkspaceQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { getRoleLabel } from 'src/api/types';
type WUser = NonNullable<WorkspaceQuery['workspace']>['users'][number];

type UseColumns = (a: { data?: WUser[]; workspace: NonNullable<WorkspaceQuery['workspace']> }) => {
  columns: ColumnDef<WUser>[];
  GTColumnSelectAnchorExported: JSX.Element;
  profileModalId: number | null;
  setProfileModalId: (id: number | null) => void;
};

const defaultColumns = ['workspace_user_name', 'email', 'role'];

export const useMembersColumns: UseColumns = ({ data = [], workspace }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.wsMembers}-columns`, defaultColumns);
  const [profileModalId, setProfileModalId] = useState<number | null>(null);
  const { profile } = useAuth();

  const { refetchQuery } = useCache();
  const resetCache = () => refetchQuery({ include: ['profile', 'workspace'] });

  const [removeUser] = useRemoveUserMutation({ onCompleted: resetCache });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<WUser>[] = [
      {
        id: 'workspace_user_name',
        header: 'Name',
        accessorFn: (row) => row.orgUnitIdentity?.name,
        cell: ({ row }) => {
          return (
            <TableUserItem
              profilePhotoPath={row.original.profilePhotoPath}
              name={`${row.original.orgUnitIdentity?.name} ${profile?.user?.id === row.original.id ? '(You)' : ''}`}
              interactive={{
                scope: 'workspace',
                workspaceId: workspace.id,
                providedUserId: row.original.id,
              }}
            />
          );
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (row) => row.orgUnitIdentity?.baseRole,
        cell: ({ row }) => getRoleLabel(row.original.orgUnitIdentity?.baseRole),
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const userId = +row.original.id;

          const canRemoveUser = row.original.workspaceUserActions?.canRemoveUser;
          const canEditProfile = row.original.workspaceUserActions?.canEditProfile;

          const onRemoveUserClick = canRemoveUser
            ? () => removeUser({ variables: { workspaceId: workspace.id, userId } })
            : undefined;

          const onEditProfileClick = canEditProfile ? () => setProfileModalId(userId) : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm onRemoveUserClick={onRemoveUserClick} onChangePermissions={onEditProfileClick} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, profile]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.wsMembers}
      options={[
        { key: 'workspace_user_name', title: 'Name' },
        { key: 'email', title: 'Email' },
        { key: 'role', title: 'Role' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    profileModalId,
    setProfileModalId,
  };
};
